import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { Logo } from './Logo'
import { NotificationBell } from './NotificationBell'
import { SMSNotificationBell } from './SMSNotificationBell'
import { ThemeToggle } from './ThemeToggle'
import { VersionInfo } from './VersionInfo'
import {
  Menu,
  X,
  Home,
  Users,
  Shield,
  Package,
  ClipboardList,
  LogOut,
  Calendar,
  Leaf,
  MessageSquare,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Info,
  BookOpen,
  Activity,
  User,
  Percent,
  Tag,
  FileText,
  Car,
  TrendingUp
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface NavChild {
  name: string
  href: string
  icon: LucideIcon
}

interface NavItem {
  name: string
  href: string
  icon: LucideIcon
  children?: NavChild[]
}

export function Layout() {
  const { profile, signOut, effectiveRole, viewMode, setViewMode } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : false
  })
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Artiklar'])

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  // Check if user is actually an admin (can switch views)
  const canSwitchView = profile?.is_admin === true
  // Use effectiveRole for determining which navigation to show
  const isAdmin = effectiveRole === 'admin'

  // Handle view mode switch
  const handleSwitchView = () => {
    const newMode = viewMode === 'admin' ? 'subcontractor' : 'admin'
    setViewMode(newMode)
    // Navigate to the appropriate dashboard
    navigate(newMode === 'admin' ? '/admin' : '/subcontractor')
  }

  const adminNavigation: NavItem[] = [
    { name: 'Uppdrag', href: '/admin/assignments', icon: Calendar },
    { name: 'Planering', href: '/admin/planning', icon: LayoutGrid },
    { name: 'Kunder', href: '/admin/customers', icon: Users },
    { name: 'Trädgårdsmästare', href: '/admin/subcontractors', icon: Leaf },
    {
      name: 'Artiklar',
      href: '/admin/articles',
      icon: Package,
      children: [
        { name: 'Kategorier', href: '/admin/articles/categories', icon: Tag },
        { name: 'Prislistor', href: '/admin/pricelists', icon: FileText },
      ]
    },
    { name: 'Veckosammanfattning', href: '/admin/weekly-summary', icon: ClipboardList },
    { name: 'Provision', href: '/admin/provisions', icon: Percent },
    { name: 'Statistik', href: '/admin/statistics', icon: TrendingUp },
    { name: 'Körjournal', href: '/admin/driving-log', icon: Car },
    { name: 'SMS', href: '/admin/communications', icon: MessageSquare },
    { name: 'Information', href: '/admin/information', icon: Info },
    { name: 'Aktivitet', href: '/admin/activity', icon: Activity },
  ]

  const toggleSubmenu = (name: string) => {
    setExpandedMenus(prev =>
      prev.includes(name)
        ? prev.filter(n => n !== name)
        : [...prev, name]
    )
  }

  const subcontractorNavigation: NavItem[] = [
    { name: 'Dashboard', href: '/subcontractor', icon: Home },
    { name: 'Mina Uppdrag', href: '/subcontractor/assignments', icon: ClipboardList },
    { name: 'Kalender', href: '/subcontractor/calendar', icon: Calendar },
    { name: 'Artiklar', href: '/subcontractor/articles', icon: Package },
    { name: 'Handbok', href: '/subcontractor/handbook', icon: BookOpen },
    { name: 'Min profil', href: '/subcontractor/profile', icon: User },
  ]

  const navigation = isAdmin ? adminNavigation : subcontractorNavigation

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-nature-200 dark:bg-nature-900 transition-colors duration-200 flex flex-col">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-nature-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-[#faf9f7] dark:bg-nature-800 transition-colors duration-200">
          <div className="flex items-center justify-between px-4 py-1 border-b border-nature-200 dark:border-nature-700 bg-[#9e7e88]">
            <div className="flex items-center justify-center flex-1">
              <Logo size="4xl" showText={false} />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-white/80 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              const hasChildren = 'children' in item && item.children
              const isExpanded = expandedMenus.includes(item.name)
              const isChildActive = hasChildren && item.children?.some(child => location.pathname === child.href)

              return (
                <div key={item.name}>
                  {hasChildren ? (
                    <>
                      <div className="flex items-center">
                        <Link
                          to={item.href}
                          onClick={() => {
                            if (!isExpanded) toggleSubmenu(item.name)
                            setSidebarOpen(false)
                          }}
                          className={`group flex flex-1 items-center px-2 py-2 text-sm font-medium rounded-l-md transition-colors duration-200 ${
                            isActive || isChildActive
                              ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                              : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                          }`}
                        >
                          <item.icon className="mr-3 h-5 w-5" />
                          {item.name}
                        </Link>
                        <button
                          onClick={() => toggleSubmenu(item.name)}
                          className={`px-2 py-2 rounded-r-md transition-colors duration-200 ${
                            isActive || isChildActive
                              ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                              : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                          }`}
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children?.map(child => {
                            const isChildItemActive = location.pathname === child.href
                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                onClick={() => setSidebarOpen(false)}
                                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                                  isChildItemActive
                                    ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                                    : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                                }`}
                              >
                                <child.icon className="mr-3 h-4 w-4" />
                                {child.name}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        isActive
                          ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                          : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                      }`}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
          <div className="border-t border-nature-200 dark:border-nature-700 p-4">
            <button
              onClick={handleSignOut}
              className="flex w-full items-center px-2 py-2 text-sm font-medium text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100 rounded-md transition-colors duration-200"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logga ut
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className="flex flex-col flex-grow bg-[#faf9f7] dark:bg-nature-800 border-r border-nature-200 dark:border-nature-700 transition-colors duration-200">
          {!sidebarCollapsed && (
            <div className="flex items-center justify-center py-1 px-4 border-b border-nature-200 dark:border-nature-700 bg-[#9e7e88]">
              <Logo size="4xl" showText={false} />
            </div>
          )}
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              const hasChildren = 'children' in item && item.children
              const isExpanded = expandedMenus.includes(item.name)
              const isChildActive = hasChildren && item.children?.some(child => location.pathname === child.href)

              return (
                <div key={item.name}>
                  {hasChildren && !sidebarCollapsed ? (
                    <>
                      <div className="flex items-center">
                        <Link
                          to={item.href}
                          onClick={() => {
                            if (!isExpanded) toggleSubmenu(item.name)
                          }}
                          className={`group flex flex-1 items-center px-2 py-2 text-sm font-medium rounded-l-md transition-colors duration-200 ${
                            isActive || isChildActive
                              ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                              : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                          }`}
                        >
                          <item.icon className="mr-3 h-5 w-5" />
                          {item.name}
                        </Link>
                        <button
                          onClick={() => toggleSubmenu(item.name)}
                          className={`px-2 py-2 rounded-r-md transition-colors duration-200 ${
                            isActive || isChildActive
                              ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                              : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                          }`}
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children?.map(child => {
                            const isChildItemActive = location.pathname === child.href
                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                                  isChildItemActive
                                    ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                                    : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                                }`}
                              >
                                <child.icon className="mr-3 h-4 w-4" />
                                {child.name}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      title={sidebarCollapsed ? item.name : undefined}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        sidebarCollapsed ? 'justify-center' : ''
                      } ${
                        isActive || isChildActive
                          ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100'
                          : 'text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100'
                      }`}
                    >
                      <item.icon className={`h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                      {!sidebarCollapsed && item.name}
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
          <div className="border-t border-nature-200 dark:border-nature-700 p-2">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expandera meny' : 'Minimera meny'}
              className={`flex w-full items-center px-2 py-2 text-sm font-medium text-nature-500 dark:text-nature-400 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-700 dark:hover:text-nature-200 rounded-md transition-colors duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <>
                  <ChevronLeft className="h-5 w-5 mr-3" />
                  Minimera meny
                </>
              )}
            </button>
            <button
              onClick={handleSignOut}
              title={sidebarCollapsed ? 'Logga ut' : undefined}
              className={`flex w-full items-center px-2 py-2 mt-1 text-sm font-medium text-nature-600 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-700 hover:text-nature-900 dark:hover:text-nature-100 rounded-md transition-colors duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <LogOut className={`h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
              {!sidebarCollapsed && 'Logga ut'}
            </button>
          </div>
        </div>
      </div>

      {/* Main content area - flex-1 to push footer down */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-nature-200 dark:border-nature-700 bg-[#faf9f7] dark:bg-nature-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 transition-colors duration-200">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-nature-700 dark:text-nature-200 lg:hidden transition-colors duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* View mode switch for admins */}
              {canSwitchView && (
                <button
                  onClick={handleSwitchView}
                  title={viewMode === 'admin' ? 'Byt till TG-vy' : 'Byt till Admin-vy'}
                  className="relative p-2 rounded-lg transition-all duration-200 hover:bg-nature-100 dark:hover:bg-nature-800 focus:outline-none focus:ring-2 focus:ring-nature-500 focus:ring-offset-2 dark:focus:ring-offset-nature-900"
                >
                  <div className="relative w-5 h-5">
                    <Shield
                      className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                        viewMode === 'admin'
                          ? 'text-primary-600 dark:text-primary-400 opacity-100 rotate-0'
                          : 'text-primary-600 dark:text-primary-400 opacity-0 -rotate-90'
                      }`}
                    />
                    <Leaf
                      className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                        viewMode === 'subcontractor'
                          ? 'text-green-600 dark:text-green-400 opacity-100 rotate-0'
                          : 'text-green-600 dark:text-green-400 opacity-0 rotate-90'
                      }`}
                    />
                  </div>
                </button>
              )}
              {isAdmin && <SMSNotificationBell />}
              <NotificationBell />
              <ThemeToggle />
              <div className="hidden sm:block sm:h-6 sm:w-px sm:bg-nature-200 dark:sm:bg-nature-700" />
              {/* Desktop: Full name and email */}
              <div className="hidden sm:flex items-center gap-x-4">
                <div className="text-sm">
                  <p className="font-medium text-nature-900 dark:text-nature-100">{profile?.full_name}</p>
                  <p className="text-nature-500 dark:text-nature-400">{profile?.email}</p>
                </div>
              </div>
              {/* Mobile: Just initials badge */}
              <div className="sm:hidden flex items-center">
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-sm font-medium text-primary-700 dark:text-primary-300">
                  {profile?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content - flex-1 to expand and push footer down */}
        <main className="py-6 flex-1">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {/* Footer - now sticks to bottom */}
        <footer className="mt-auto border-t border-nature-200 dark:border-nature-700 bg-[#faf9f7] dark:bg-nature-800 transition-colors duration-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
              {/* Copyright */}
              <div className="text-sm text-nature-500 dark:text-nature-400">
                © {new Date().getFullYear()} MB Trädgård Support AB
              </div>
              
              {/* Version Info */}
              <div className="flex items-center space-x-4">
                <VersionInfo position="footer" />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
